/**
 * User authentication menu component.
 * Anonymous users can use the app freely.
 * Logged-in users see their account info and can sign out.
 * Supports GitHub/Google OAuth and email/password registration.
 */

import { useEffect, useState } from "react";
import {
  Button,
  Menu,
  Text,
  Loader,
  Modal,
  TextInput,
  PasswordInput,
  Stack,
  Anchor,
  Alert,
  Divider,
} from "@mantine/core";
import {
  IconUser,
  IconLogin,
  IconUserPlus,
  IconBrandGithub,
  IconBrandGoogle,
} from "@tabler/icons-react";
import { useUser, useUserLoading, useViewerStore } from "../store";
import { apiClient, ApiClientError } from "../api";
import type { AuthConfig } from "../api";

type AuthView = "login" | "register";

export function UserMenu() {
  const user = useUser();
  const loading = useUserLoading();
  const setUser = useViewerStore((state) => state.setUser);
  const setUserLoading = useViewerStore((state) => state.setUserLoading);

  const [modalOpen, setModalOpen] = useState(false);
  const [authView, setAuthView] = useState<AuthView>("login");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null);

  // Login form
  const [loginId, setLoginId] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  // Register form
  const [regUsername, setRegUsername] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regConfirmPassword, setRegConfirmPassword] = useState("");
  const [regDisplayName, setRegDisplayName] = useState("");

  // Fetch user info and auth config on mount
  useEffect(() => {
    const init = async () => {
      setUserLoading(true);
      try {
        const [userInfo, config] = await Promise.all([
          apiClient.getCurrentUser(),
          apiClient.getAuthConfig().catch(() => null),
        ]);
        setUser(userInfo.authenticated ? userInfo : null);
        if (config) {
          setAuthConfig(config);
        }
      } catch {
        setUser(null);
      } finally {
        setUserLoading(false);
      }
    };
    init();
  }, [setUser, setUserLoading]);

  // Check for OAuth error/success in URL (from redirect)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authError = params.get("auth_error");
    if (authError) {
      setError(`Login failed: ${authError.replace(/_/g, " ")}`);
      setModalOpen(true);
      const url = new URL(window.location.href);
      url.searchParams.delete("auth_error");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  const resetForms = () => {
    setLoginId("");
    setLoginPassword("");
    setRegUsername("");
    setRegEmail("");
    setRegPassword("");
    setRegConfirmPassword("");
    setRegDisplayName("");
    setError(null);
  };

  const openModal = (view: AuthView) => {
    resetForms();
    setAuthView(view);
    setModalOpen(true);
  };

  const handleLogin = async () => {
    setError(null);
    if (!loginId || !loginPassword) {
      setError("Please fill in all fields");
      return;
    }
    setSubmitting(true);
    try {
      const userInfo = await apiClient.login({
        username_or_email: loginId,
        password: loginPassword,
      });
      setUser(userInfo);
      setModalOpen(false);
      resetForms();
    } catch (err) {
      setError(
        err instanceof ApiClientError
          ? err.detail || "Login failed"
          : "Login failed"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegister = async () => {
    setError(null);
    if (!regUsername || !regEmail || !regPassword) {
      setError("Please fill in all required fields");
      return;
    }
    if (regPassword !== regConfirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (regPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    setSubmitting(true);
    try {
      const userInfo = await apiClient.register({
        username: regUsername,
        email: regEmail,
        password: regPassword,
        display_name: regDisplayName || undefined,
      });
      setUser(userInfo);
      setModalOpen(false);
      resetForms();
    } catch (err) {
      setError(
        err instanceof ApiClientError
          ? err.detail || "Registration failed"
          : "Registration failed"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await apiClient.logout();
    } catch {
      // Clear local state even if request fails
    }
    setUser(null);
  };

  const hasOAuth = authConfig?.github_enabled || authConfig?.google_enabled;

  if (loading) {
    return <Loader size="xs" />;
  }

  // Authenticated user — show account menu
  if (user) {
    return (
      <Menu shadow="md" width={200}>
        <Menu.Target>
          <Button size="xs" variant="subtle" leftSection={<IconUser size={14} />}>
            {user.display_name || user.username}
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>Account</Menu.Label>
          <Menu.Item disabled>
            <Text size="xs" c="dimmed">@{user.username}</Text>
          </Menu.Item>
          {user.email && (
            <Menu.Item disabled>
              <Text size="xs" c="dimmed">{user.email}</Text>
            </Menu.Item>
          )}
          <Menu.Divider />
          <Menu.Item color="red" onClick={handleLogout}>
            Sign out
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    );
  }

  // Anonymous user — show sign-in button + modal
  return (
    <>
      <Button
        size="compact-sm"
        variant="filled"
        leftSection={<IconLogin size={14} />}
        onClick={() => openModal("login")}
      >
        Sign in
      </Button>

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={authView === "login" ? "Sign in" : "Create account"}
        centered
        size="sm"
      >
        {error && (
          <Alert color="red" mb="md" variant="light">
            {error}
          </Alert>
        )}

        {/* OAuth buttons */}
        {hasOAuth && (
          <>
            <Stack gap="xs">
              {authConfig?.github_enabled && (
                <Button
                  fullWidth
                  variant="default"
                  leftSection={<IconBrandGithub size={18} />}
                  onClick={() => apiClient.loginWithGithub()}
                >
                  {authView === "login" ? "Continue with GitHub" : "Sign up with GitHub"}
                </Button>
              )}
              {authConfig?.google_enabled && (
                <Button
                  fullWidth
                  variant="default"
                  leftSection={<IconBrandGoogle size={18} />}
                  onClick={() => apiClient.loginWithGoogle()}
                >
                  {authView === "login" ? "Continue with Google" : "Sign up with Google"}
                </Button>
              )}
            </Stack>
            <Divider
              my="md"
              label={authView === "login" ? "or sign in with email" : "or register with email"}
              labelPosition="center"
            />
          </>
        )}

        {authView === "login" ? (
          <Stack gap="sm">
            <TextInput
              label="Username or email"
              placeholder="Enter your username or email"
              value={loginId}
              onChange={(e) => setLoginId(e.currentTarget.value)}
              onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            />
            <PasswordInput
              label="Password"
              placeholder="Enter your password"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.currentTarget.value)}
              onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            />
            <Button fullWidth onClick={handleLogin} loading={submitting} mt="xs">
              Sign in
            </Button>
            <Text size="sm" ta="center" c="dimmed">
              Don&apos;t have an account?{" "}
              <Anchor
                component="button"
                size="sm"
                onClick={() => { setError(null); setAuthView("register"); }}
              >
                Register
              </Anchor>
            </Text>
          </Stack>
        ) : (
          <Stack gap="sm">
            <TextInput
              label="Username"
              placeholder="Choose a username"
              required
              value={regUsername}
              onChange={(e) => setRegUsername(e.currentTarget.value)}
            />
            <TextInput
              label="Email"
              placeholder="your@email.com"
              required
              value={regEmail}
              onChange={(e) => setRegEmail(e.currentTarget.value)}
            />
            <TextInput
              label="Display name"
              placeholder="How you want to appear (optional)"
              value={regDisplayName}
              onChange={(e) => setRegDisplayName(e.currentTarget.value)}
            />
            <PasswordInput
              label="Password"
              placeholder="At least 8 characters"
              required
              value={regPassword}
              onChange={(e) => setRegPassword(e.currentTarget.value)}
            />
            <PasswordInput
              label="Confirm password"
              placeholder="Re-enter your password"
              required
              value={regConfirmPassword}
              onChange={(e) => setRegConfirmPassword(e.currentTarget.value)}
              onKeyDown={(e) => e.key === "Enter" && handleRegister()}
            />
            <Button
              fullWidth
              leftSection={<IconUserPlus size={16} />}
              onClick={handleRegister}
              loading={submitting}
              mt="xs"
            >
              Create account
            </Button>
            <Text size="sm" ta="center" c="dimmed">
              Already have an account?{" "}
              <Anchor
                component="button"
                size="sm"
                onClick={() => { setError(null); setAuthView("login"); }}
              >
                Sign in
              </Anchor>
            </Text>
          </Stack>
        )}
      </Modal>
    </>
  );
}
